import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, UserCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/common/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { getErrorMessage } from "@/lib/api";

const CARD_ANIM = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.3 } };

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // idle | accepting | done | error
  const [message, setMessage] = useState("");

  const accept = useCallback(async () => {
    setStatus("accepting");
    try {
      await api.post("/team/accept", { token });
      sessionStorage.removeItem("hg_pending_invite");
      setStatus("done");
      toast.success("You've joined the team!");
      setTimeout(() => navigate("/app/dashboard"), 1200);
    } catch (e) {
      setStatus("error");
      setMessage(getErrorMessage(e, "This invite is invalid or has already been used."));
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No invite token provided.");
      return;
    }
    // Persist token so login/signup can return here after authentication.
    sessionStorage.setItem("hg_pending_invite", token);
    if (authLoading) return;
    if (!user) return; // wait for the user to authenticate
    accept();
  }, [token, user, authLoading, accept]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <motion.div {...CARD_ANIM} className="w-full max-w-md rounded-2xl border bg-card p-8 text-center" data-testid="accept-invite-card">
        <div className="mb-6 flex justify-center"><Logo /></div>

        {status === "error" ? (
          <>
            <AlertCircle className="mx-auto h-10 w-10 text-[hsl(var(--risk-high))]" />
            <h1 className="mt-4 font-display text-xl font-semibold">Invite problem</h1>
            <p className="mt-2 text-sm text-muted-foreground" data-testid="accept-invite-error">{message}</p>
            <Link to="/app/dashboard"><Button className="mt-6" variant="outline">Go to dashboard</Button></Link>
          </>
        ) : status === "done" ? (
          <>
            <UserCheck className="mx-auto h-10 w-10 text-[hsl(var(--risk-low))]" />
            <h1 className="mt-4 font-display text-xl font-semibold">You're in!</h1>
            <p className="mt-2 text-sm text-muted-foreground">Redirecting you to your dashboard…</p>
          </>
        ) : authLoading || status === "accepting" ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
            <h1 className="mt-4 font-display text-xl font-semibold">Joining the team…</h1>
          </>
        ) : !user ? (
          <>
            <UserCheck className="mx-auto h-10 w-10 text-primary" />
            <h1 className="mt-4 font-display text-xl font-semibold">You've been invited to a team</h1>
            <p className="mt-2 text-sm text-muted-foreground">Log in or create an account with your invited email to join the portfolio.</p>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/login"><Button className="w-full" data-testid="accept-invite-login">Log in to accept</Button></Link>
              <Link to="/signup"><Button variant="outline" className="w-full" data-testid="accept-invite-signup">Create an account</Button></Link>
            </div>
          </>
        ) : (
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
        )}
      </motion.div>
    </div>
  );
}
